<template>
    <!-- display:contents keeps a single root (the card, footer and modal stay siblings for layout). -->
    <div class="contents">
        <div :class="rootClass">
            <div class="flex shrink-0 items-start justify-between gap-3 bg-brand-header px-8 py-4 text-white">
                <asset-short-info :asset="workOrder.asset" />
                <language-selector v-if="isExternalSession" class="shrink-0" :use-full-label="true" />
            </div>
            <div :class="bodyClass">
                <div class="mb-11 flex items-center justify-between gap-3">
                    <div class="flex min-w-0 items-center gap-3">
                        <work-order-tag />
                        <div class="truncate text-brand-header underline">{{ workOrder?.code }}</div>
                    </div>
                    <div class="flex items-center gap-3">
                        <jet-select
                            v-if="priorityEnabled"
                            v-model="form.priority"
                            :disabled="!isPriorityEditable || (priorities?.data || []).length === 0"
                            :horizontal="false"
                            :inline="true"
                            :invalid-feedback="form.errors.priority"
                            :options="priorities?.data || []"
                            :readonly="!isPriorityEditable || priorities.length === 0"
                            :required="true"
                            class="!mb-0 h-8 rounded-[8px] border-1 px-2 py-1"
                        >
                            <template #readonly>
                                <span v-if="workOrder">
                                    {{ workOrder?.priority.label }}
                                </span>
                                <span v-else>{{ trans('general.none') }}</span>
                            </template>
                        </jet-select>

                        <jet-select
                            v-model="form.status"
                            :disabled="!fieldsEditable || (statuses?.data || []).length < 2"
                            :horizontal="false"
                            :inline="true"
                            :invalid-feedback="form.errors.status"
                            :options="statuses?.data || []"
                            :readonly="!fieldsEditable || statuses.length < 2"
                            :required="true"
                            class="!mb-0 h-8 rounded-[8px] border-1 px-2 py-1"
                            name="status"
                        >
                            <template #readonly>
                                {{ workOrder?.status?.label || trans('general.none') }}
                            </template>
                        </jet-select>
                    </div>
                </div>

                <inoperability-reminder-checkbox
                    v-if="inoperableReminderVisible"
                    v-model="form.metadata.inoperability_tag_acknowledged"
                    :reminder-key="inoperableReminderKey"
                    :readonly="true"
                    id="inoperability-tag-acknowledged-external"
                    class="mb-4"
                />

                <collapsable class="mb-7">
                    <template #toggler>
                        <h3 class="mb-1 !leading-[22px] !font-bold">
                            <inline-editable-field
                                v-model="form.title"
                                :editable="fieldsEditable"
                                :invalid-feedback="form.errors.title"
                                :line-height="24"
                                :placeholder="trans('workorder.form.title.placeholder')"
                            />
                        </h3>
                    </template>

                    <inline-editable-field
                        v-model="form.description"
                        :editable="fieldsEditable"
                        :invalid-feedback="form.errors.description"
                        :max-height="220"
                        :placeholder="trans('workorder.form.description.placeholder')"
                        :rows="2"
                        class="mb-4"
                        name="description"
                    />

                    <case-category-section
                        v-if="catsaExtrasEnabled"
                        v-model:category="form.metadata.case_category"
                        v-model:subcategory="form.metadata.case_subcategory"
                        :asset-type="workOrder.asset?.type?.value"
                        :categories="caseCategories"
                        :category-readonly="!isCaseCategoryEditable"
                        :readonly="!fieldsEditable"
                        class="mb-5"
                    />

                    <troubleshooting-section
                        v-if="troubleshootingEnabled"
                        v-model="form.metadata.troubleshooting"
                        :readonly="!fieldsEditable"
                        class="mb-5"
                    />

                    <msd-notification-section
                        v-if="msdNotificationEnabled"
                        v-model="form.metadata.msd_notification"
                        :readonly="!fieldsEditable"
                        class="mb-5"
                    />

                    <impact-assessment-section
                        v-if="impactAssessmentEnabled"
                        v-model="form.metadata.impact_assessment"
                        :readonly="!fieldsEditable"
                        class="mb-5"
                    />

                    <work-order-info
                        :assignees="groupedAssignees || []"
                        :can-reassign="canReassign"
                        :catsa-extras="catsaExtras"
                        :form="form"
                        :is-editable="fieldsEditable"
                        :priorities="priorities?.data || []"
                        :statuses="statuses?.data || []"
                        :work-order="workOrder"
                        :work-order-property-config="workOrderPropertyConfig"
                    />

                    <dl class="mb-0">
                        <div class="row">
                            <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                                {{
                                    trans(
                                        catsaExtrasEnabled
                                            ? 'workorder.details.workorder.case_id.label'
                                            : 'workorder.details.workorder.ticket_id.label',
                                    )
                                }}
                            </dt>
                            <dd class="col-sm-8 col-12 text-muted">
                                <inline-editable-field
                                    v-model="form.ticketId"
                                    :editable="fieldsEditable"
                                    :invalid-feedback="form.errors.ticketId"
                                    :line-height="22"
                                    :placeholder="
                                        trans(catsaExtrasEnabled ? 'workorder.form.case_id.placeholder' : 'workorder.form.ticket_id.placeholder')
                                    "
                                    :resizable="false"
                                />
                            </dd>
                        </div>
                        <div class="row">
                            <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                                {{ trans('workorder.details.workorder.ticket_link.label') }}
                            </dt>
                            <dd class="col-sm-8 col-12 mb-0 text-muted">
                                <inline-editable-field
                                    v-model="form.ticketLink"
                                    :editable="fieldsEditable"
                                    :invalid-feedback="form.errors.ticketLink"
                                    :line-height="22"
                                    :placeholder="trans('workorder.form.ticket_link.placeholder')"
                                    :resizable="false"
                                />
                            </dd>
                        </div>
                    </dl>
                </collapsable>

                <collapsable class="mb-7">
                    <template #toggler>
                        <h3 class="mb-1 text-xl !leading-[20px] !font-bold">
                            {{ trans('workorder.details.section.attachments.title') }}
                            <em v-if="attachmentsRequired" class="text-danger">*</em>
                        </h3>
                    </template>

                    <!-- A done work order only accepts additions — existing attachments can no longer be renamed or deleted. -->
                    <file-list
                        :attachments="workOrder?.attachments"
                        :can-delete="hasAnyPermission('workorder.attachment.delete') && !isDone"
                        :can-edit="hasAnyPermission('workorder.attachment.update') && !isDone"
                        :can-read="hasAnyPermission(['workorder.attachment.index', 'workorder.attachment.read'])"
                        :readonly="false"
                        class="mb-3"
                        @deleted="loadFormData()"
                        @updated="loadFormData()"
                    />
                    <file-upload
                        v-if="isEditable"
                        v-model="form.attachments"
                        :accepted-file-types="attachmentUiConfig.allowedFileTypes"
                        :class="{ 'is-invalid': form.errors.attachments }"
                        :drop-area-hint="attachmentUiConfig.dropAreaHint"
                        :drop-area-label="attachmentUiConfig.dropAreaLabel"
                        :url="route('attachments.store')"
                        @upload-started="uploadInProgress = true"
                        @upload-completed="uploadInProgress = false"
                    />
                    <div class="invalid-feedback">
                        {{ form.errors.attachments }}
                    </div>
                </collapsable>

                <collapsable v-if="workOrder" class="mb-4">
                    <template #toggler>
                        <h3 class="mb-4 text-xl !leading-[20px] !font-bold">
                            {{ trans('workorder.details.section.comments.title') }}
                        </h3>
                    </template>
                    <comments
                        :create-comment-url="
                            route('workorders.comments.store', {
                                workorder: workOrder.id,
                            })
                        "
                        :editable="true"
                        :get-comments-url="
                            route('workorders.comments.index', {
                                workorder: workOrder,
                            })
                        "
                        :initial-comments="workOrder?.comments"
                        :placeholder="trans('workorder.details.section.comments.placeholder')"
                        :poll-interval="15000"
                        :show-warning="showCommentWarning"
                        @input="updateComment"
                    />
                </collapsable>

                <div v-if="workOrderHistory" class="-mx-8 bg-gray-lightest px-8 py-4">
                    <work-order-activity-log :workorder-history="workOrderHistory" />
                </div>
            </div>

            <div class="sticky bottom-0 z-10 flex shrink-0 items-center gap-4 border-t-1 bg-white px-8 py-4">
                <template v-if="isExternalSession">
                    <jet-button
                        v-if="!error && isEditable"
                        :disabled="!form.isDirty || uploadInProgress || isRequiredPropertyMissing"
                        :is-processing="form.processing"
                        color="primary"
                        style="flex: 1"
                        @click.stop="ensureCommentPosted(submitForm)"
                    >
                        {{ trans('general.action.save') }}
                    </jet-button>
                    <p v-else class="flex-1 text-center text-sm text-gray-600">
                        {{ trans('external.close_hint.idle') }}
                    </p>
                </template>
                <template v-else>
                    <jet-button
                        v-if="closeMode !== 'none'"
                        :disabled="uploadInProgress"
                        style="flex: 1"
                        variant="secondary"
                        @click.stop="ensureCommentPosted(closePage)"
                        >{{ trans('general.action.close') }}
                    </jet-button>
                    <jet-button
                        v-if="!error && isEditable"
                        :disabled="!form.isDirty || uploadInProgress || isRequiredPropertyMissing"
                        :is-processing="form.processing"
                        color="primary"
                        style="flex: 1"
                        @click.stop="ensureCommentPosted(submitForm)"
                    >
                        {{ trans('general.action.save') }}
                    </jet-button>
                </template>
            </div>
            <div class="flex items-center justify-center">
                <flash-message class="!transform-none" />
            </div>
        </div>
        <the-footer v-if="showFooter" class="mx-auto max-w-2xl border-0" />
        <resolve-preventative-work-order-modal ref="resolvePreventativeModalRef" name="resolve-preventative-page" />

        <dialog-modal :show="showReassignWarning" type="warning" @close="showReassignWarning = false">
            <template #title>{{ trans('external.reassign_warning.title') }}</template>
            <div>{{ trans('external.reassign_warning.body') }}</div>
            <template #footer>
                <jet-button type="button" variant="tertiary" @click.stop="showReassignWarning = false">
                    {{ trans('general.action.cancel') }}
                </jet-button>
                <jet-button color="primary" @click.stop="confirmReassignAway">
                    {{ trans('external.reassign_warning.confirm') }}
                </jet-button>
            </template>
        </dialog-modal>
    </div>
</template>

<script setup>
import JetButton from '@/Jetstream/Button.vue';
import DialogModal from '@/Jetstream/DialogModal.vue';
import WorkOrderInfo from '@/Pages/WorkOrders/Modals/WorkOrderInfo.vue';
import FileUpload from '@/Components/FileUpload.vue';
import InlineEditableField from '@/Pages/WorkOrders/Modals/InlineEditableField.vue';
import FileList from '@/Components/FileList.vue';
import Comments from '@/Components/Comments.vue';
import { eventBus, events } from '@/eventBus.js';
import { computed, nextTick, ref, watch, watchEffect } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n.js';
import AssetShortInfo from '@/Components/AssetShortInfo.vue';
import LanguageSelector from '@/Containers/LanguageSelector.vue';
import JetSelect from '@/Jetstream/Select.vue';
import Collapsable from '@/Components/Collapsable.vue';
import WorkOrderActivityLog from '@/Pages/Modals/WorkOrder/WorkOrderActivityLog.vue';
import TheFooter from '@/Containers/TheFooter.vue';
import FlashMessage from '@/Components/FlashMessage.vue';
import WorkOrderTag from '@/Components/CustomTags/WorkOrderTag.vue';
import { useSessionStorage } from '@/composables/useSessionStorage';
import {
    isCaseCategoryComplete,
    isMsdNotificationComplete,
    isTroubleshootingComplete,
    visibleCaseCategories,
} from '@/Pages/WorkOrders/Modals/catsaExtras.js';
import { isImpactAssessmentComplete } from '@/Pages/WorkOrders/Modals/impactAssessment.js';
import { useWorkOrderSectionGates } from '@/composables/useWorkOrderSectionGates';
import { useInoperableReminder } from '@/composables/useInoperableReminder';
import InoperabilityReminderCheckbox from '@/Pages/WorkOrders/Modals/InoperabilityReminderCheckbox.vue';
import MsdNotificationSection from '@/Pages/WorkOrders/Modals/MsdNotificationSection.vue';
import ImpactAssessmentSection from '@/Pages/WorkOrders/Modals/ImpactAssessmentSection.vue';
import CaseCategorySection from '@/Pages/WorkOrders/Modals/CaseCategorySection.vue';
import TroubleshootingSection from '@/Pages/WorkOrders/Modals/TroubleshootingSection.vue';
import ResolvePreventativeWorkOrderModal from '@/Pages/WorkOrders/Modals/ResolvePreventativeWorkOrderModal.vue';
import { hasFeature } from '@/mixins/hasFeature.js';

const props = defineProps({
    workOrder: {
        type: Object,
        required: true,
    },
    external: Boolean,
    externalTabToken: { type: String, default: null },
    workOrderHistory: Array,
    statuses: {
        type: [Object, null],
        required: true,
    },
    priorities: {
        type: [Object, null],
        required: true,
    },
    assignees: {
        type: [Array, null],
        required: true,
    },
    isEditable: {
        type: Boolean,
        required: true,
    },
    canReassign: {
        type: Boolean,
        default: true,
    },
    canUpdateAssetOperability: {
        type: Boolean,
        default: false,
    },
    workOrderPropertyConfig: {
        type: Object,
        default: () => ({}),
    },
    impactAssessment: { type: Object },
    catsaExtras: { type: Object },
    msdNotification: { type: Object },
    defaultContactPhone: { type: [String, null], default: null },
    caseCategories: { type: Array, default: () => [] },
    inoperableReminderKey: { type: [String, null], default: null },
    // Layout: drop the centered-card chrome so the detail fills its container (two-pane board).
    embedded: { type: Boolean, default: false },
    // The two-pane page renders a single shared footer, so the embedded detail omits its own.
    showFooter: { type: Boolean, default: true },
    // Explicit close behaviour: 'window' closes the tab, 'none' hides the close button.
    closeMode: {
        type: String,
        default: 'window',
        validator: (value) => ['window', 'none'].includes(value),
    },
});

const sessionStorage = useSessionStorage();

const rootClass = computed(() =>
    props.embedded
        ? 'flex h-full min-h-0 flex-col overflow-hidden bg-white'
        : 'mx-auto my-4 max-h-[calc(100vh-5rem)] max-w-2xl overflow-auto rounded-xl border bg-white shadow',
);
const bodyClass = computed(() => (props.embedded ? 'min-h-0 flex-1 overflow-y-auto px-8 py-4' : 'px-8 py-4'));

const resolvePreventativeModalRef = ref(null);
const initialStatus = props.workOrder.status?.value;
const initialAssignee = props.workOrder.assignee?.id || 'unassigned';
const showReassignWarning = ref(false);
const attachmentUiConfig = computed(() => props.workOrderPropertyConfig?.attachments?.ui ?? {});
const attachmentsRequired = computed(() => attachmentUiConfig.value.required ?? false);
const { catsaExtrasEnabled, troubleshootingEnabled, msdNotificationEnabled, impactAssessmentEnabled } = useWorkOrderSectionGates({
    workOrderType: () => props.workOrder.type?.value,
    asset: () => props.workOrder.asset,
    caseCategory: () => form.metadata.case_category ?? null,
    catsaExtras: props.catsaExtras,
    msdNotification: props.msdNotification,
    impactAssessment: props.impactAssessment,
});
const caseCategories = computed(() =>
    visibleCaseCategories(props.caseCategories, form.metadata.case_category, props.catsaExtras, props.canUpdateAssetOperability),
);

const { visible: inoperableReminderVisible } = useInoperableReminder({
    reminderKey: () => props.inoperableReminderKey,
    workOrderType: () => props.workOrder.type?.value,
    workOrderStatus: () => props.workOrder.status?.value,
    caseCategory: () => form.metadata.case_category ?? null,
    previousCaseCategory: () => props.workOrder.caseCategory ?? null,
    assetIsInoperable: () => props.workOrder.asset?.status?.operability === 'inoperable',
    catsaExtras: props.catsaExtras,
});

const groupedAssignees = computed(
    () =>
        (props.assignees || []).map((assignee) => {
            return {
                group: assignee.group,
                data: assignee.data.map((data) => ({
                    label: data.name,
                    value: data.id,
                })),
            };
        }) ?? [],
);

const form = useForm({
    status: props.workOrder.status?.value || null,
    priority: props.workOrder.priority?.value || 'medium',
    assignee: props.workOrder.assignee?.id || 'unassigned',
    startDate: props.workOrder.startDate || null,
    dueDate: props.workOrder.dueDate || null,
    resolvedDate: props.workOrder.performedAt || null,
    mspRespondedAt: props.workOrder.mspRespondedAt || null,
    title: props.workOrder.title || null,
    description: props.workOrder.description || null,
    ticketId: props.workOrder.ticketId || null,
    ticketLink: props.workOrder.ticketLink || null,
    attachments: [],
    metadata: {
        impact_assessment: { ...(props.workOrder.impactAssessment ?? {}) },
        msd_notification: { ...(props.workOrder.msdNotification ?? {}) },
        troubleshooting: { ...(props.workOrder.troubleshooting ?? {}) },
        contact_phone: props.workOrder.contactPhone ?? props.defaultContactPhone ?? null,
        case_category: props.workOrder.caseCategory ?? null,
        case_subcategory: props.workOrder.caseSubcategory ?? null,
        inoperability_tag_acknowledged: props.workOrder.inoperabilityTagAcknowledged ?? false,
    },
});

const requiresResolvedDate = computed(
    () => props.workOrder.type?.value === 'preventative_maintenance' && hasFeature('schedule_preventative_maintenance_work_orders'),
);

const isImpactAssessmentMissing = computed(() => impactAssessmentEnabled.value && !isImpactAssessmentComplete(form.metadata.impact_assessment));
const isMsdNotificationMissing = computed(() => msdNotificationEnabled.value && !isMsdNotificationComplete(form.metadata.msd_notification));
const isTroubleshootingMissing = computed(() => troubleshootingEnabled.value && !isTroubleshootingComplete(form.metadata.troubleshooting));
const isCaseCategoryMissing = computed(
    () =>
        catsaExtrasEnabled.value &&
        !!form.metadata.case_category &&
        !isCaseCategoryComplete(form.metadata.case_category, form.metadata.case_subcategory),
);

const isRequiredPropertyMissing = computed(
    () =>
        (attachmentsRequired.value && !form.attachments?.length && !props.workOrder?.attachments?.length) ||
        isImpactAssessmentMissing.value ||
        isMsdNotificationMissing.value ||
        isTroubleshootingMissing.value ||
        isCaseCategoryMissing.value,
);

watch(
    () => form.assignee,
    (newAssignee) => {
        router.reload({
            only: ['workOrderPropertyConfig'],
            data: { assignee: newAssignee },
            preserveScroll: true,
        });
    },
);

const error = ref(null);
const commentDraft = ref(null);
const showCommentWarning = ref(false);
const uploadInProgress = ref(false);
const isDone = computed(() => props.workOrder.status?.value === 'done');
// A completed work order locks every field; only comments and attachments stay editable.
const fieldsEditable = computed(() => props.isEditable && !isDone.value);
const isPriorityEditable = computed(() => fieldsEditable.value && !isExternalSession.value);
// Backend sends null priorities when the work_order_priority feature is off; this page is
// external-only, so we derive visibility from the prop rather than client-side feature flags.
const priorityEnabled = computed(() => props.priorities !== null);
const isCaseCategoryEditable = computed(() => props.isEditable && form.status === 'in_progress' && form.assignee && form.assignee !== 'unassigned');

function submitForm() {
    if (!props.workOrder.id || !props.isEditable) {
        return;
    }

    // An external actor reassigning the work order away from themselves loses it from their board
    // (the board is scoped to the assignee). Confirm before saving.
    if (isReassigningAway.value) {
        showReassignWarning.value = true;
        return;
    }

    continueSubmit();
}

function continueSubmit() {
    if (form.status === 'done' && requiresResolvedDate.value && !form.resolvedDate) {
        resolvePreventativeModalRef.value?.open({
            workOrder: props.workOrder,
            onConfirm: (resolvedDate) => {
                form.resolvedDate = resolvedDate;
                performSubmit();
            },
        });

        return;
    }

    performSubmit();
}

function confirmReassignAway() {
    showReassignWarning.value = false;
    continueSubmit();
}

function performSubmit() {
    form.transform((data) => {
        if (!data.attachments) {
            delete data.attachments;
        }

        delete data.priority;

        if (data.status !== 'done' || !data.resolvedDate) {
            delete data.resolvedDate;
        }

        return data;
    }).put(route('workorders.update', { workorder: props.workOrder.id }), {
        preserveScroll: true,
        onSuccess: () => {
            form.attachments = [];
            eventBus.$emit(events.workOrderUpdated, props.workOrder.id);

            if (initialStatus !== form.status) {
                eventBus.$emit(events.workOrderStatusUpdated, {
                    workOrderId: props.workOrder.id,
                    status: form.status,
                });
            }
        },
    });
}

function closePage() {
    if (props.closeMode === 'none') {
        return;
    }

    window.close();
}

function loadFormData() {
    router.reload({ preserveScroll: true, only: ['workOrder'] });
}

const updateComment = (comment) => {
    commentDraft.value = comment;
    if (!commentDraft.value) {
        showCommentWarning.value = false;
    }
};
const ensureCommentPosted = (callback) => {
    if (!commentDraft.value) {
        callback();
        return;
    }

    if (showCommentWarning.value) {
        nextTick(() => {
            callback();
        });
    }

    showCommentWarning.value = !!commentDraft.value;
};

const isExternalSession = computed(() => props.external);
// Only on the assignee-scoped board (embedded), where the work order is currently the actor's:
// changing the assignee hands it to someone else, so it drops off their board. The legacy single
// work-order page (non-embedded) has no board and may legitimately prefill a new assignee.
const isReassigningAway = computed(() => isExternalSession.value && props.embedded && form.assignee !== initialAssignee);

watchEffect(() => {
    if (props.externalTabToken) {
        sessionStorage.setItem('external_tab_token', props.externalTabToken);
    }
});
</script>
